import React, { useState } from 'react';
// import { Link as RouterLink } from 'react-router-dom'; // Removed unused import
// import { useModal } from '../contexts/ModalContext'; // If CTAs open a modal

// Import Heroicons (or your preferred icon library)
import { PhoneIcon, EnvelopeIcon, MapPinIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ContactUsPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: 'General Inquiry', // Default subject
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // null, 'success', or 'error'
  const [formErrors, setFormErrors] = useState({});

  // const { openModal } = useModal(); // Example if using modal for a CTA

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required.';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (formData.phone.trim() && !/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
        errors.phone = 'Please enter a valid phone number (e.g., (XXX) XXX-XXXX).';
    }
    if (!formData.message.trim()) {
      errors.message = 'Message is required.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionStatus(null);

    try {
      // Simulate API call
      console.log('Submitting form data:', formData);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate network delay
      
      // Replace with actual API endpoint:
      // const response = await fetch('/api/contact-submissions', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData),
      // });

      // if (!response.ok) {
      //   throw new Error('Network response was not ok');
      // }
      
      // const result = await response.json();
      // console.log('Submission successful:', result);
      
      setSubmissionStatus('success');
      setFormData({ fullName: '', email: '', phone: '', subject: 'General Inquiry', message: '' }); // Reset form
      setFormErrors({});
    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Contact details - replace with actual info from a config file or direct input
  const qualityAirHoustonInfo = {
    phone: '(713) 922-5702',
    emergencyPhone: '(713) 922-5702', // Assuming emergency is same as main, update if different
    email: 'pejmanansaripoor@gmail.com',
    address: '6023 Pennworth Ln, Houston, TX 77084',
    googleMapsLink: 'https://maps.app.goo.gl/ceTrc4GajWgZJeDq7',
    hours: [
      { day: 'Monday - Friday', time: '8:00 AM - 6:00 PM' }, // Keeping placeholder hours, update if needed
      { day: 'Saturday', time: '9:00 AM - 2:00 PM' }, // Keeping placeholder hours, update if needed
      { day: 'Sunday', time: 'Closed' }, // Keeping placeholder hours, update if needed
    ],
    emergencyService: '24/7 Available'
  };


  return (
    <div className="bg-brand-background min-h-screen">
      {/* Header Section */}
      <header 
        className="py-16 md:py-24 text-center bg-gray-100 relative bg-cover bg-center"
        style={{ backgroundImage: "url('/images/contact.png')" }} 
      >
        <div className="absolute inset-0 bg-black opacity-50"></div> {/* Overlay for text readability */}
        <div className="container mx-auto px-4 relative z-10"> {/* Ensure text is above overlay */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4"> {/* Changed text to white */}
            Get In Touch
          </h1><br></br>
          <br></br>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto"> {/* Changed text to light gray */}
          We're here to help with your heating, ventilation, and air conditioning needs in Greater Houston. If you have questions, need service, or want a free estimate, please reach out. We look forward to hearing from you!
          </p>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Column 1: Contact Form */}
            <section className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold text-brand-gray-dark mb-6">Send Us a Message</h2>
              
              {submissionStatus === 'success' && (
                <div className="mb-6 p-4 bg-brand-green-light text-brand-green-dark rounded-md">
                  Thank you for your message! We'll get back to you within 24 business hours.
                </div>
              )}
              {submissionStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
                  Sorry, there was an error sending your message. Please try again or call us.
                </div>
              )}

              {submissionStatus !== 'success' && (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 gap-y-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-brand-gray-dark">Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="fullName"
                        id="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="e.g., Alex Garcia"
                        required
                        className={`mt-1 block w-full px-3 py-2 border ${formErrors.fullName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-brand-accent focus:border-brand-accent sm:text-sm`}
                      />
                      {formErrors.fullName && <p className="mt-1 text-sm text-red-500">{formErrors.fullName}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-brand-gray-dark">Email Address <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="youremail@example.com"
                        required
                        className={`mt-1 block w-full px-3 py-2 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-brand-accent focus:border-brand-accent sm:text-sm`}
                      />
                      {formErrors.email && <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-brand-gray-dark">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(XXX) XXX-XXXX"
                        className={`mt-1 block w-full px-3 py-2 border ${formErrors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-brand-accent focus:border-brand-accent sm:text-sm`}
                      />
                      {formErrors.phone && <p className="mt-1 text-sm text-red-500">{formErrors.phone}</p>}
                      <p className="mt-1 text-xs text-brand-gray">Optional, but helps us reach you faster.</p>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-brand-gray-dark">How Can We Help You? <span className="text-red-500">*</span></label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand-accent focus:border-brand-accent sm:text-sm rounded-md"
                      >
                        <option>General Inquiry</option>
                        <option>Service Request</option>
                        <option>Quote for New System</option>
                        <option>Feedback</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-brand-gray-dark">Your Message <span className="text-red-500">*</span></label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Please type your message here..."
                        required
                        className={`mt-1 block w-full px-3 py-2 border ${formErrors.message ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-brand-accent focus:border-brand-accent sm:text-sm`}
                      ></textarea>
                      {formErrors.message && <p className="mt-1 text-sm text-red-500">{formErrors.message}</p>}
                    </div>
                  </div>

                  <div className="mt-8">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-brand-accent hover:bg-brand-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </section>

            {/* Column 2: Direct Contact Info, Hours, Emergency */}
            <section className="space-y-8">
              <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-semibold text-brand-gray-dark mb-6">Direct Contact Details</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="flex items-center text-lg font-medium text-brand-gray-dark">
                      <PhoneIcon className="h-6 w-6 mr-3 text-brand-green" />
                      Call Us:
                    </h3>
                    <a href={`tel:${qualityAirHoustonInfo.phone}`} className="text-brand-green hover:text-brand-green-dark text-lg ml-9">
                      {qualityAirHoustonInfo.phone}
                    </a>
                  </div>
                  <div>
                    <h3 className="flex items-center text-lg font-medium text-brand-gray-dark">
                      <EnvelopeIcon className="h-6 w-6 mr-3 text-brand-green" />
                      Email Us:
                    </h3>
                    <a href={`mailto:${qualityAirHoustonInfo.email}`} className="text-brand-green hover:text-brand-green-dark text-lg ml-9 break-all">
                      {qualityAirHoustonInfo.email}
                    </a>
                  </div>
                  {qualityAirHoustonInfo.address && (
                    <div>
                      <h3 className="flex items-center text-lg font-medium text-brand-gray-dark">
                        <MapPinIcon className="h-6 w-6 mr-3 text-brand-green" />
                        Our Office:
                      </h3>
                      <p className="text-brand-gray text-lg ml-9">
                        {qualityAirHoustonInfo.address}
                        {/* Optional: Add a note like "(By Appointment Only)" */}
                      </p>
                       <a 
                        href={qualityAirHoustonInfo.googleMapsLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-brand-green hover:text-brand-green-dark ml-9"
                      >
                        View on Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-semibold text-brand-gray-dark mb-6">Our Houston Office Hours</h2>
                <div className="space-y-2">
                  {qualityAirHoustonInfo.hours.map((item, index) => (
                    <div key={index} className="flex items-center text-brand-gray">
                      <ClockIcon className="h-5 w-5 mr-3 text-brand-green flex-shrink-0" />
                      <span className="font-medium w-28">{item.day}:</span>
                      <span>{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {qualityAirHoustonInfo.emergencyPhone && (
                <div className="bg-red-50 p-6 rounded-lg shadow-lg border border-red-200">
                  <h2 className="flex items-center text-xl font-semibold text-red-700 mb-3">
                    <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-red-600" />
                    For Emergencies:
                  </h2>
                  <p className="text-red-600 mb-2">
                    If you have an HVAC emergency outside of business hours, please call our 24/7 Emergency Line:
                  </p>
                  <a href={`tel:${qualityAirHoustonInfo.emergencyPhone}`} className="text-red-700 hover:text-red-800 font-bold text-lg">
                    <PhoneIcon className="inline h-5 w-5 mr-1" /> {qualityAirHoustonInfo.emergencyPhone}
                  </a>
                  {qualityAirHoustonInfo.emergencyService && <p className="text-sm text-red-500 mt-2">{qualityAirHoustonInfo.emergencyService}</p>}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* Embedded Map Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-brand-gray-dark text-center mb-8">
            Our Service Area
          </h2>
          <div className="aspect-w-16 aspect-h-9 rounded-lg shadow-lg overflow-hidden">
            {/* Replace with your actual Google Maps embed code */}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3460.521968672038!2d-95.68905858488954!3d29.84970798195207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640d0cd0d9c77ff%3A0xb1a8f8f8ddf99583!2s6023%20Pennworth%20Ln%2C%20Houston%2C%20TX%2077084!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus" // Updated GMap Embed URL for specific address
              width="100%"
              height="450"
              style={{ border:0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Quality Air Houston Location Map"
            ></iframe>
          </div>
           <p className="text-center mt-4 text-brand-gray text-sm">
            TEXAS
          </p>
        </div>
      </section>

      {/* Optional: Social Media Links Section - if not in footer */}
      {/* 
      <section className="py-8 bg-brand-background">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-semibold text-brand-gray-dark mb-4">Connect With Us</h3>
          <div className="flex justify-center space-x-4">
            <a href="#" className="text-brand-gray hover:text-brand-green"> Facebook Icon </a>
            <a href="#" className="text-brand-gray hover:text-brand-green"> LinkedIn Icon </a>
            <a href="#" className="text-brand-gray hover:text-brand-green"> Yelp Icon </a>
          </div>
        </div>
      </section>
      */}
    </div>
  );
};

export default ContactUsPage; 